// src/data/articles/qr-code-digital-menu.js
// All language versions of the "QR code digital menu / ScanQur" article.

export default [
  // ── English ───────────────────────────────────────────────────────────────
  {
    slug: 'qr-code-digital-menu',
    lang: 'en',
    translations: { en: 'qr-code-digital-menu', fr: 'menu-digital-qr-code', es: 'menu-digital-codigo-qr', ar: 'qaimat-raqmiya-bi-ramz-qr' },
    title: 'QR Code Digital Menus: How ScanQur Helps Restaurants and Shops Drop the Paper Menu',
    seoTitle: 'QR Code Digital Menu: Create and Share Your Menu with ScanQur',
    seoDescription:
      'How a QR code digital menu works, and how ScanQur lets restaurants and shops update prices in real time, take WhatsApp orders and publish in several languages.',
    imageAlt: 'A QR code on the left resolving into a digital menu displayed on a smartphone screen',
    headerTint: ['rgba(2,132,199,0.28)', 'rgba(3,105,161,0.34)'],
    publishedDate: '2026-09-04',
    readingMinutes: 14,
    relatedCategories: ['food', 'work', 'tech'],
    relatedArticles: ['benefits-of-laughter', 'types-of-humor', 'history-of-humor'],
    intro: [
      "A printed menu is a snapshot. It's right the day it comes back from the printer, and a little less right every week after that. A supplier puts his prices up, a dish comes off, and the summer card is late again.",
      "Digital menus are the obvious fix, and QR codes have sat on tables long enough now that nobody reads them as a health measure any more. What changed is the tooling. You no longer need a developer, or a subscription that only makes sense if you own forty restaurants.",
      "Below: what you build, what your customer sees, and what the whole thing costs. Which, for most merchants, is nothing.",
    ],
    sections: [
      {
        number: 1,
        heading: 'The paper menu is the slowest part of the business',
        anchor: 'the-paper-menu-problem',
        content: [
          { type: 'paragraph', text: 'Most things in a small business change in an afternoon. You move a table. You switch supplier. You put a sign in the window. The menu is the exception, because changing it means going back to the printer.' },
          { type: 'lead', text: 'A printed card locks you into:' },
          {
            type: 'list',
            items: [
              "Prices that move at the printer's speed, not your supplier's",
              'A reprint bill every time an ingredient goes up',
              'Biro corrections and stickers over the old prices',
              'Sold-out dishes explained one table at a time',
              'One language, for everyone who walks in',
            ],
          },
          { type: 'paragraph', text: "None of it is dramatic. It's a few hours a month and a print bill, spent keeping a piece of paper honest." },
        ],
      },
      {
        number: 2,
        heading: 'What ScanQur is',
        anchor: 'what-is-scanqur',
        content: [
          {
            type: 'rich',
            parts: [
              { text: 'ScanQur builds a digital menu or product catalogue and puts it behind a QR code. Its English site walks through how to ' },
              { text: 'create a free QR code menu for a restaurant or shop', href: 'https://scanqur.com/en' },
              { text: ". It was made for independent businesses, not chains: restaurants, cafés, bakeries and bars, but also groceries, butchers, pharmacies, salons, and any shop that just needs a price list people can read on their phone." },
            ],
          },
          { type: 'paragraph', text: 'The menu lives at a fixed address, scanqur.com/shop/your-business, and the QR code points there. Not at a version of your menu. At your menu. So you can edit it all week and the printed code still works.' },
          { type: 'lead', text: 'Two things worth knowing up front:' },
          {
            type: 'list',
            items: [
              'Your customers install nothing. The code opens a web page in their phone browser',
              "The core product is free, and not in the trial sense: there's no card to enter and no clock running. The paid plan buys unlimited languages, more staff accounts, a custom domain and a longer analytics history",
            ],
          },
        ],
      },
      {
        number: 3,
        heading: 'What the customer does',
        anchor: 'the-customer-experience',
        content: [
          { type: 'lead', text: "From the guest's side it's three steps:" },
          {
            type: 'list',
            items: [
              'They point a phone camera at the code on the table, the window or the counter',
              'The menu opens in the browser, with photos, descriptions and current prices',
              'They browse, switch language if they need to, and either just read or place an order',
            ],
          },
          { type: 'paragraph', text: 'Ordering is optional. A menu can run in browse-only mode, which suits a shop that only wants a catalogue, or a restaurant that would rather orders went through its waiters.' },
          { type: 'paragraph', text: "And because it's an ordinary web page, the link works away from the table too. An Instagram bio, a Google Business profile, or a WhatsApp reply when someone asks whether you do anything vegetarian." },
        ],
      },
      {
        number: 4,
        heading: 'Building the catalogue',
        anchor: 'building-the-catalogue',
        content: [
          { type: 'paragraph', text: 'A ScanQur menu is categories holding products, which is already how you think about the paper card.' },
          {
            type: 'list',
            items: [
              'Categories in your own words: Starters, Mains, Desserts, or Bread, Pastry, Cakes',
              'Products with a name, a price, a description and a photo',
              'Dietary and allergen labels such as halal, vegetarian, spicy or contains sesame',
              'An availability switch per product, so a sold-out dish disappears without being deleted',
              'A search field on the public menu, which starts to matter past fifty items',
            ],
          },
          { type: 'paragraph', text: "Retyping an existing menu is the job everyone puts off, so there is a scanner for it. Photograph the printed card and it pulls out the categories, dishes and prices. You check its work instead of doing the typing. A second assistant will draft a product description, or a social post about a dish, if writing forty of them yourself doesn't appeal. You read it before it goes anywhere." },
          { type: 'paragraph', text: 'There is also a quality score that reads the menu back to you before you publish and flags the gaps: a category with no prices, products with no photo. Not much of a feature, but it stops the first version your guests see from being the half-finished one.' },
          { type: 'paragraph', text: 'Products and categories are unlimited on every plan, free included. What the paid plan buys is capacity around the menu (languages, staff, analytics history), not menu size.' },
        ],
      },
      {
        number: 5,
        heading: 'Updating prices and products in real time',
        anchor: 'real-time-updates',
        content: [
          { type: 'paragraph', text: 'This is the part that pays back the setup, and the reason a real digital menu beats a PDF of the paper one.' },
          {
            type: 'list',
            items: [
              'Change a price and it is live on the next scan',
              'Mark a dish sold out for the evening and bring it back tomorrow',
              'Publish the seasonal card the day it starts, not the week the printer delivers',
              'Reorder categories so the dishes you want to sell are the ones people see first',
            ],
          },
          { type: 'paragraph', text: 'The QR code never changes. Print it on a table tent, a window sticker, the back of a business card, and it stays good for the whole life of the menu behind it. Worth being clear about that difference: a QR generator gives you a code that points at a file, whereas a menu platform gives you one that points at something you keep editing.' },
        ],
      },
      {
        number: 6,
        heading: 'Making the menu look like the business',
        anchor: 'design-and-themes',
        content: [
          { type: 'paragraph', text: 'A digital menu that looks like a spreadsheet undoes whatever the room is trying to say. ScanQur handles that with themes instead of a design tool, so it stays a five-minute decision.' },
          {
            type: 'list',
            items: [
              'A theme that sets the layout and the typography',
              'Your logo at the top of the public menu',
              'Colours and fonts adjusted to match your signage',
              'A header image, usually a photo of the room or a signature dish',
            ],
          },
          { type: 'paragraph', text: 'Photos are worth the hour they cost. A menu with pictures gets read differently from a list of names and prices: people linger on it, and the dishes nobody can picture stop being invisible.' },
        ],
      },
      {
        number: 7,
        heading: 'QR codes you can put on a table',
        anchor: 'custom-qr-codes',
        content: [
          { type: 'paragraph', text: 'The code is signage. ScanQur treats it that way, not as a black-and-white square you download once and forget about.' },
          {
            type: 'list',
            items: [
              'Your logo in the centre of the code',
              'Your brand colours instead of default black',
              'A frame carrying a short instruction, so guests know what the code opens',
              'One code per table, each with its printed table label',
              'A print-ready PDF containing every table code at once',
              'An HD download for a sign maker or a printer',
            ],
          },
          { type: 'paragraph', text: 'The per-table codes are what make table ordering possible: the code is how an order knows where it came from.' },
          { type: 'paragraph', text: "And if a trip to the printer isn't how you want to spend the week, you can order the table tents, stickers and frames already printed, from vetted partners, inside the dashboard." },
        ],
      },
      {
        number: 8,
        heading: 'Taking orders: WhatsApp, tables and the counter',
        anchor: 'taking-orders',
        content: [
          { type: 'paragraph', text: "Ordering is where the menu stops being a document and becomes part of the service. These are channels you switch on, and you are never signed up to a checkout you didn't want." },
          { type: 'lead', text: 'WhatsApp ordering goes like this:' },
          {
            type: 'list',
            items: [
              'The guest browses the menu and adds items to a cart',
              'They tap the WhatsApp button, and their app opens with a message already listing every item, quantity and the total',
              'They can add a note, then send it to your business number',
              'You receive a structured order inside a conversation you can reply to, and confirm a pickup time or payment instructions in the same thread',
            ],
          },
          { type: 'paragraph', text: 'No commission on any of it, and your till stays exactly as it is. The channel suits anything prepared for a counter, a pickup, or a delivery you run yourself: takeaway, bakery pre-orders, a grocery basket put aside for the afternoon, a caterer working a day ahead.' },
          { type: 'paragraph', text: 'Table ordering is the dining-room version. Each table has its own code, so the order lands in your dashboard already tagged with the table number and the floor keeps moving.' },
          { type: 'paragraph', text: "The customer isn't left wondering, either. Once they have sent an order they can follow its status on the same page, without an account and without installing anything." },
          { type: 'lead', text: 'On the management side:' },
          {
            type: 'list',
            items: [
              'A live feed of incoming orders',
              'Completion or cancellation with a recorded reason',
              'A history you can go back through',
              'Monthly reporting on revenue, average order value and cancellation rate',
            ],
          },
          { type: 'paragraph', text: "None of this has to land on one person's phone. You invite staff as manager, kitchen, waiter, host or floor, and each role sees only the part of the dashboard its job needs. It is also how you get out of the shared-password habit most small teams end up in." },
        ],
      },
      {
        number: 9,
        heading: 'Reservations, and the questions people phone to ask',
        anchor: 'reservations-and-business-info',
        content: [
          { type: 'paragraph', text: "Whoever is reading tonight's menu is often the person who will book a table for next week. ScanQur lets that happen on the page they are already looking at, without sending them to a second platform and without a fee on every cover." },
          { type: 'paragraph', text: "Bookings run either as a self-service form or in a simpler phone-only mode, if you would rather keep that conversation on the line. Either way an email reaches you the moment a table is taken." },
          { type: 'lead', text: 'The public menu also carries the things people ring up to ask:' },
          {
            type: 'list',
            items: [
              'Business name, logo and a short description',
              'Address with a map link',
              'Phone, WhatsApp and email',
              'Opening hours, with an indicator showing whether you are open now',
              'Social links: Instagram, Facebook, TikTok, YouTube',
            ],
          },
          { type: 'paragraph', text: 'It absorbs a lot of interruptions mid-service, and it makes the menu the one link worth putting everywhere you exist online.' },
        ],
      },
      {
        number: 10,
        heading: 'Turning first-timers into regulars',
        anchor: 'loyalty-and-reviews',
        content: [
          { type: 'paragraph', text: 'The menu is already where the relationship with a customer happens, so two things live there that normally need their own tools.' },
          { type: 'lead', text: 'The loyalty stamp card first:' },
          {
            type: 'list',
            items: [
              'Stamps come from orders you already mark as done, so there is nothing extra to run',
              'The customer collects them on their own phone, without an app or an account',
              'It works without collecting a phone number or an email',
              'After a few visits they claim a free reward',
            ],
          },
          { type: 'paragraph', text: 'Then ratings. Customers review you on the menu itself, where they were already looking, instead of on a platform they would have to go and find. A review you disagree with can be reported but not deleted, which is the only reason the rating means anything to the next person reading it.' },
          { type: 'paragraph', text: 'Neither of these turns your menu into an app. They work because the customer is already there, holding the phone they ordered with.' },
        ],
      },
      {
        number: 11,
        heading: 'One code, several languages',
        anchor: 'multilingual-menus',
        content: [
          { type: 'paragraph', text: "A printed menu picks a language, or splits into versions you then have to reprint together. A digital one doesn't have that problem." },
          { type: 'paragraph', text: 'ScanQur publishes in seven languages (English, French, Arabic, Spanish, Portuguese, German, Italian), with proper right-to-left support for Arabic and not Arabic text poured into a layout built the other way round. Guests switch with a tap, translations can be generated for you, and every version stays in step when you change a price.' },
          { type: 'paragraph', text: 'Free covers two languages, paid removes the cap. For a business in a tourist area, or one whose street does not all speak the same language, this is usually the feature that settles it.' },
        ],
      },
      {
        number: 12,
        heading: 'Analytics: what guests look at',
        anchor: 'menu-analytics',
        content: [
          { type: 'paragraph', text: "A paper menu tells you nothing about how it gets read. You know what sold. You don't know what was considered and passed over." },
          { type: 'lead', text: 'A digital menu reports on itself:' },
          {
            type: 'list',
            items: [
              'Views: how often the menu is opened, and at what times',
              'Unique visitors, so you can tell traffic from repeat browsing',
              'Top products by the attention they get',
              'Top categories, showing where attention concentrates',
            ],
          },
          { type: 'paragraph', text: 'Each figure is shown against the previous period, so you are reading a direction and not a bare number.' },
          { type: 'paragraph', text: 'The useful part comes from putting those numbers next to your till. A dish with plenty of views and few orders usually has a price problem, or a photo problem, or a description problem. Rarely a recipe problem. A category nobody opens might just be buried too far down the page.' },
          { type: 'paragraph', text: 'Two things keep this honest. Judge trends, not days. And give it time: two normal weeks before a pattern means anything, a month before you would act on a comparison. History runs thirty days on the free plan, a year on the paid one.' },
        ],
      },
      {
        number: 13,
        heading: 'Which businesses this fits',
        anchor: 'who-its-for',
        content: [
          { type: 'paragraph', text: 'The obvious ones are food and drink, and there are dedicated setups for them: restaurants, cafés, bakeries, bars, fast food, pizzerias.' },
          { type: 'lead', text: 'The less obvious ones work the same way, because a menu and a catalogue are the same object:' },
          {
            type: 'list',
            items: [
              'Groceries and butchers publishing a price list that moves weekly',
              'Pharmacies listing what they stock',
              'Beauty salons showing services and durations',
              'Clothing and electronics shops running a catalogue customers can browse in store',
            ],
          },
          { type: 'paragraph', text: 'What they have in common is a list of things with prices that move more often than reprinting them is convenient.' },
        ],
      },
      {
        number: 14,
        heading: 'What changes day to day',
        anchor: 'what-changes',
        content: [
          { type: 'lead', text: 'Strip the feature list away and here is what a merchant gets:' },
          {
            type: 'list',
            items: [
              'Less dependence on printed menus, and on the bill that comes with them',
              'Price and product changes that take a minute instead of a print run',
              'A current experience for customers, without asking them to install anything',
              'A menu that opens instantly on the phone already in their hand',
              'Products presented with photos and a layout that matches the business',
              'Several languages behind one code, kept in step automatically',
              'Orders and bookings that arrive structured instead of shouted across a room',
              'Hours, address, phone and social links on the page people already open',
              'A single link worth sharing everywhere you exist online',
              'A stamp card that brings customers back, with nothing for them to install',
              'Ratings and reviews collected where people already are, on the menu itself',
              'Time back, because the menu stops being a task and becomes a setting you adjust',
            ],
          },
        ],
      },
    ],
    tips: {
      heading: 'Getting the switch right',
      intro: 'The platform is the easy part. What separates a menu people use from one that gets scanned once is usually the setup around it.',
      items: [
        "Start from the real menu, not the ideal one. Publish what you're serving this week",
        'Photograph the dishes that sell before the rest; a few good photos beat forty poor ones',
        'Put the code where a hand naturally lands: table tent, counter edge, window at eye level',
        'Add a short instruction on the frame, because a bare code tells nobody what it opens',
        'Keep availability current for the first fortnight, until updating it becomes a reflex',
        'Share the menu link online as well, so the code is not the only way in',
      ],
      outro: "Once those are in place the menu looks after itself, in about the time you'd have spent explaining a sold-out dish.",
    },
    faq: [
      { q: 'Do customers need an app to open a QR menu?', a: 'No. Phone cameras read QR codes natively, and the code opens a normal web page in the browser. There is nothing to install on either side of the table.' },
      { q: 'Does the QR code change when I edit my menu?', a: 'No. The code points at a permanent address for your menu, not at a version of it. You might reprint a code because it wore out. Never because your prices changed.' },
      { q: 'Is ScanQur really free?', a: 'The core product is free, with no card and no time limit, and menu items and categories are unlimited on that plan. The paid plan adds unlimited languages, more team members, a custom domain, dashboard order and reservation management, and a longer analytics history.' },
      { q: 'How many languages can a menu have?', a: 'Seven: English, French, Arabic, Spanish, Portuguese, German and Italian, with real right-to-left rendering for Arabic. The free plan covers two at a time; the paid plan removes the limit.' },
      { q: 'Can I take orders without a point-of-sale system?', a: 'Yes. Orders arrive through WhatsApp as a structured, pre-filled message, or through per-table QR codes into a dashboard feed. No commission is taken and your existing payment method stays as it is.' },
      { q: 'Do customers need an account to order, review or collect loyalty stamps?', a: 'No. Placing an order, following its status afterwards, leaving a review and filling a loyalty stamp card all happen on the menu page itself, with no app, no account and no phone number collected.' },
      { q: 'Does this work for a shop rather than a restaurant?', a: 'Yes. A catalogue behaves like a menu, so groceries, butchers, pharmacies, salons, clothing and electronics shops use the same categories, products and prices, with ordering switched off if all they need is a browsable price list.' },
    ],
    conclusion: [
      "A digital menu doesn't change how you run your business. It moves one specific thing, your price list, off paper (where changing it costs money and time) and onto a page you can edit from your phone between two services.",
      'What comes out of that is ordinary and useful. Prices that are right, sold-out dishes flagged in seconds, several languages behind one code, orders that arrive legible, and a first idea of what people read before they choose.',
      {
        parts: [
          { text: "If your card is due a reprint soon, that's the moment to try the other thing. Building a " },
          { text: 'digital menu for your restaurant or shop', href: 'https://scanqur.com/en' },
          { text: ' and printing its code costs nothing, and the worst case is that you end up with an up-to-date link to hand out next to the printed card you were going to order anyway.' },
        ],
      },
    ],
  },

  // ── French ────────────────────────────────────────────────────────────────
  {
    slug: 'menu-digital-qr-code',
    lang: 'fr',
    translations: { en: 'qr-code-digital-menu', fr: 'menu-digital-qr-code', es: 'menu-digital-codigo-qr', ar: 'qaimat-raqmiya-bi-ramz-qr' },
    title: "Menu digital par QR code : comment ScanQur remplace la carte papier des commerces",
    seoTitle: "Menu digital QR code : créer sa carte en ligne avec ScanQur",
    seoDescription:
      "Menu digital par QR code : comment ScanQur permet aux restaurants et commerces de modifier prix et produits en temps réel, en plusieurs langues.",
    imageAlt: "Un QR code à gauche menant à un menu digital affiché sur l'écran d'un smartphone",
    headerTint: ['rgba(2,132,199,0.28)', 'rgba(3,105,161,0.34)'],
    publishedDate: '2026-09-04',
    readingMinutes: 14,
    relatedCategories: ['food', 'work', 'tech'],
    relatedArticles: ['bienfaits-du-rire', 'types-dhumour', 'histoire-de-lhumour'],
    intro: [
      "Une carte imprimée est un instantané. Elle est juste le jour où elle sort de chez l'imprimeur, un peu moins juste chaque semaine qui suit. Un fournisseur augmente, un plat sort de la carte, et le menu d'été arrive encore en retard.",
      "Le menu digital est la réponse évidente, et le QR code traîne sur les tables depuis assez longtemps pour que plus personne n'y voie une mesure sanitaire. Ce qui a changé, ce sont les outils. Il ne faut plus ni développeur, ni abonnement qui ne se justifie qu'à partir de quarante établissements.",
      "Ci-dessous : ce que vous construisez, ce que voit votre client, et ce que ça coûte. C'est-à-dire, pour la plupart des commerces, rien.",
    ],
    sections: [
      {
        number: 1,
        heading: "La carte papier est la partie la plus lente du commerce",
        anchor: 'probleme-carte-papier',
        content: [
          { type: 'paragraph', text: "Dans un commerce, presque tout change en un après-midi. Vous déplacez une table. Vous changez de fournisseur. Vous collez une affiche en vitrine. La carte fait exception, parce que la modifier veut dire retourner chez l'imprimeur." },
          { type: 'lead', text: "Une carte imprimée vous enferme dans :" },
          {
            type: 'list',
            items: [
              "Des prix qui suivent le rythme de l'imprimeur, pas celui de vos fournisseurs",
              "Une facture de réimpression dès qu'un ingrédient augmente",
              "Des corrections au stylo et des étiquettes collées sur les anciens prix",
              "Des plats épuisés qu'il faut expliquer table par table",
              "Une seule langue, pour tous ceux qui poussent la porte",
            ],
          },
          { type: 'paragraph', text: "Rien de dramatique. Quelques heures par mois et un budget d'impression, dépensés à maintenir une feuille de papier à jour." },
        ],
      },
      {
        number: 2,
        heading: "Ce qu'est ScanQur",
        anchor: 'qu-est-ce-que-scanqur',
        content: [
          {
            type: 'rich',
            parts: [
              { text: "ScanQur construit un menu ou un catalogue produits digital et le place derrière un QR code. Sa page française explique comment " },
              { text: 'créer un menu digital par QR code gratuit', href: 'https://scanqur.com/fr' },
              { text: ". L'outil vise les commerces indépendants, pas les chaînes : restaurants, cafés, boulangeries et bars, mais aussi épiceries, boucheries, pharmacies, salons, et toute boutique qui a juste besoin d'une liste de prix lisible sur un téléphone." },
            ],
          },
          { type: 'paragraph', text: "Le menu vit à une adresse fixe, scanqur.com/shop/votre-commerce, et le QR code pointe là. Pas vers une version de votre carte. Vers votre carte. Vous pouvez donc la modifier toute la semaine, le code imprimé continue de marcher." },
          { type: 'lead', text: "Deux choses à savoir d'entrée :" },
          {
            type: 'list',
            items: [
              "Vos clients n'installent rien. Le code ouvre une page web dans le navigateur du téléphone",
              "Le produit de base est gratuit, et pas au sens « essai » : il n'y a pas de carte bancaire à saisir et pas de compte à rebours. L'offre payante achète les langues illimitées, plus de comptes pour l'équipe, un domaine personnalisé et un historique de statistiques plus long",
            ],
          },
        ],
      },
      {
        number: 3,
        heading: "Ce que fait le client",
        anchor: 'experience-client',
        content: [
          { type: 'lead', text: "Côté client, ça tient en trois gestes :" },
          {
            type: 'list',
            items: [
              "Il pointe l'appareil photo de son téléphone vers le code, sur la table, en vitrine ou au comptoir",
              "Le menu s'ouvre dans son navigateur, avec les photos, les descriptions et les prix du jour",
              "Il parcourt la carte, change de langue si besoin, puis se contente de lire ou passe commande",
            ],
          },
          { type: 'paragraph', text: "La commande reste optionnelle. Un menu peut tourner en consultation seule, ce qui convient à une boutique qui veut juste un catalogue, ou à un restaurant qui préfère que les commandes passent par ses serveurs." },
          { type: 'paragraph', text: "Et comme c'est une page web ordinaire, le lien sert aussi loin de la table. Une bio Instagram, une fiche Google Business, ou une réponse WhatsApp quand quelqu'un demande si vous faites du végétarien." },
        ],
      },
      {
        number: 4,
        heading: "Construire le catalogue",
        anchor: 'construire-le-catalogue',
        content: [
          { type: 'paragraph', text: "Un menu ScanQur, ce sont des catégories qui contiennent des produits. C'est déjà comme ça que vous pensez votre carte papier." },
          {
            type: 'list',
            items: [
              "Des catégories dans vos mots : Entrées, Plats, Desserts, ou Pains, Viennoiseries, Gâteaux",
              "Des produits avec un nom, un prix, une description et une photo",
              "Des mentions alimentaires et allergènes : halal, végétarien, épicé, contient du sésame",
              "Un interrupteur de disponibilité par produit, pour qu'un plat épuisé disparaisse sans être supprimé",
              "Une recherche sur le menu public, qui commence à compter au-delà de cinquante références",
            ],
          },
          { type: 'paragraph', text: "Retaper une carte existante, c'est la corvée que tout le monde repousse. Il y a donc un scanner pour ça. Vous photographiez la carte imprimée, il en sort les catégories, les plats et les prix. Vous vérifiez son travail au lieu de faire la saisie. Un second assistant rédige une description produit, ou un post pour les réseaux, si en écrire quarante ne vous tente pas. Vous relisez avant que ça parte." },
          { type: 'paragraph', text: "Il y a aussi un score de qualité qui vous relit la carte avant publication et pointe les trous : une catégorie sans prix, des produits sans photo. Pas vraiment une fonctionnalité, mais ça évite que la première version vue par vos clients soit celle à moitié faite." },
          { type: 'paragraph', text: "Les produits et les catégories sont illimités sur toutes les offres, gratuite comprise. Ce que l'offre payante achète, c'est de la capacité autour du menu (langues, équipe, historique), pas de la taille de carte." },
        ],
      },
      {
        number: 5,
        heading: "Modifier les prix et les produits en temps réel",
        anchor: 'mise-a-jour-temps-reel',
        content: [
          { type: 'paragraph', text: "C'est la partie qui rembourse la mise en place, et ce qui distingue un vrai menu digital d'un PDF de la carte papier." },
          {
            type: 'list',
            items: [
              "Un prix modifié est en ligne au scan suivant",
              "Un plat marqué épuisé pour le service revient dès demain",
              "La carte de saison paraît le jour où elle commence, pas la semaine où l'imprimeur livre",
              "Les catégories se réordonnent pour que ce que vous voulez vendre soit vu en premier",
            ],
          },
          { type: 'paragraph', text: "Le QR code, lui, ne bouge jamais. Imprimez-le sur un chevalet, un adhésif de vitrine, le dos d'une carte de visite : il reste valable pendant toute la vie du menu qu'il ouvre. La nuance mérite d'être dite : un générateur de QR code vous donne un code qui pointe vers un fichier, là où une plateforme de menu vous en donne un qui pointe vers quelque chose que vous continuez d'éditer." },
        ],
      },
      {
        number: 6,
        heading: "Donner au menu l'apparence du commerce",
        anchor: 'design-et-templates',
        content: [
          { type: 'paragraph', text: "Un menu digital qui ressemble à un tableur annule tout ce que la salle essaie de dire. ScanQur règle ça par des templates plutôt qu'un outil de design, donc la décision reste à cinq minutes." },
          {
            type: 'list',
            items: [
              "Un template qui fixe la mise en page et la typographie",
              "Votre logo en tête du menu public",
              "Des couleurs et des polices ajustées à votre enseigne",
              "Une image d'en-tête, souvent une photo de la salle ou d'un plat signature",
            ],
          },
          { type: 'paragraph', text: "Les photos valent l'heure qu'elles coûtent. Un menu illustré ne se lit pas comme une liste de noms et de prix : les clients s'y attardent, et les plats qu'on n'arrive pas à se représenter cessent d'être invisibles." },
        ],
      },
      {
        number: 7,
        heading: "Des QR codes présentables sur une table",
        anchor: 'qr-codes-personnalisables',
        content: [
          { type: 'paragraph', text: "Le code, c'est de la signalétique. ScanQur le traite comme tel, pas comme un carré noir et blanc qu'on télécharge une fois et qu'on oublie." },
          {
            type: 'list',
            items: [
              "Votre logo au centre du code",
              "Vos couleurs à la place du noir par défaut",
              "Un cadre avec une consigne courte, pour que le client sache ce qu'il ouvre",
              "Un code par table, avec son étiquette de table imprimée",
              "Un PDF prêt à imprimer contenant tous les codes de tables",
              "Un téléchargement haute définition pour un imprimeur ou un enseigniste",
            ],
          },
          { type: 'paragraph', text: "Ce sont ces codes par table qui rendent la commande à table possible : le code est ce qui permet à une commande de savoir d'où elle vient." },
          { type: 'paragraph', text: "Et si passer chez l'imprimeur ne vous tente pas, vous pouvez commander les chevalets, adhésifs et cadres déjà imprimés, auprès de partenaires sélectionnés, depuis le tableau de bord." },
        ],
      },
      {
        number: 8,
        heading: "Prendre les commandes : WhatsApp, tables et comptoir",
        anchor: 'commandes',
        content: [
          { type: 'paragraph', text: "La commande, c'est le moment où le menu cesse d'être un document pour devenir une partie du service. Ce sont des canaux que vous activez, et vous ne vous retrouvez jamais embarqué dans un tunnel de paiement que vous n'aviez pas demandé." },
          { type: 'lead', text: "La commande WhatsApp, ça donne ça :" },
          {
            type: 'list',
            items: [
              "Le client parcourt le menu et ajoute des articles à un panier",
              "Il appuie sur le bouton WhatsApp, et son application s'ouvre sur un message déjà rempli avec chaque article, la quantité et le total",
              "Il peut ajouter une note, puis l'envoyer à votre numéro professionnel",
              "Vous recevez une commande structurée dans une conversation où vous pouvez répondre, et confirmer une heure de retrait ou le mode de paiement dans le même fil",
            ],
          },
          { type: 'paragraph', text: "Aucune commission là-dessus, et votre encaissement reste exactement ce qu'il est. Le canal convient à tout ce qui se prépare pour un comptoir, un retrait ou une livraison que vous assurez vous-même : vente à emporter, précommandes de boulangerie, un panier d'épicerie mis de côté pour l'après-midi, un traiteur qui travaille la veille." },
          { type: 'paragraph', text: "La commande à table en est la version salle. Chaque table a son code, la commande arrive donc dans votre tableau de bord déjà associée au numéro de table, et le service continue." },
          { type: 'paragraph', text: "Le client n'est pas laissé dans le flou non plus. Une fois sa commande envoyée, il en suit l'état sur la même page, sans compte et sans rien installer." },
          { type: 'lead', text: "Côté gestion :" },
          {
            type: 'list',
            items: [
              "Un flux en direct des commandes qui arrivent",
              "Une validation ou une annulation avec motif enregistré",
              "Un historique consultable",
              "Un rapport mensuel sur le chiffre d'affaires, le panier moyen et le taux d'annulation",
            ],
          },
          { type: 'paragraph', text: "Rien de tout ça n'a à tomber sur le téléphone d'une seule personne. Vous invitez l'équipe avec des rôles, gérant, cuisine, serveur, accueil ou salle, et chacun ne voit que la partie du tableau de bord dont son poste a besoin. C'est aussi comme ça qu'on sort du mot de passe partagé où finissent la plupart des petites équipes." },
        ],
      },
      {
        number: 9,
        heading: "Réservations, et les questions pour lesquelles on vous appelle",
        anchor: 'reservations-et-infos',
        content: [
          { type: 'paragraph', text: "Celui qui lit la carte ce soir est souvent celui qui réservera une table la semaine prochaine. ScanQur lui permet de le faire sur la page qu'il a déjà sous les yeux, sans l'envoyer sur une seconde plateforme et sans frais sur chaque couvert." },
          { type: 'paragraph', text: "Les réservations fonctionnent soit en libre-service, soit dans un mode téléphone uniquement, si vous préférez garder cet échange à l'oral. Dans les deux cas, un e-mail vous parvient dès qu'une table est prise." },
          { type: 'lead', text: "Le menu public porte aussi ce pour quoi on vous téléphone :" },
          {
            type: 'list',
            items: [
              "Nom du commerce, logo et courte description",
              "Adresse avec un lien vers la carte",
              "Téléphone, WhatsApp et e-mail",
              "Horaires d'ouverture, avec un indicateur précisant si vous êtes ouvert maintenant",
              "Liens vers vos réseaux : Instagram, Facebook, TikTok, YouTube",
            ],
          },
          { type: 'paragraph', text: "Ça absorbe discrètement beaucoup d'interruptions en plein service, et ça fait du menu le seul lien à diffuser partout où vous existez en ligne." },
        ],
      },
      {
        number: 10,
        heading: "Transformer un client de passage en habitué",
        anchor: 'fidelite-et-avis',
        content: [
          { type: 'paragraph', text: "La carte est déjà l'endroit où se joue la relation avec le client. Deux choses y logent donc, alors qu'elles demandent d'habitude leurs propres outils." },
          { type: 'lead', text: "La carte de fidélité à tampons, d'abord :" },
          {
            type: 'list',
            items: [
              "Les tampons viennent des commandes que vous marquez déjà comme terminées, il n'y a donc rien de plus à gérer",
              "Le client les cumule sur son propre téléphone, sans application ni compte",
              "Ça fonctionne sans collecter de numéro de téléphone ni d'e-mail",
              "Au bout de quelques visites, il réclame une récompense gratuite",
            ],
          },
          { type: 'paragraph', text: "Ensuite les avis. Les clients vous notent sur la carte elle-même, là où ils regardaient déjà, au lieu d'une plateforme qu'il faudrait aller chercher. Un avis que vous contestez se signale mais ne se supprime pas, et c'est la seule raison pour laquelle la note vaut encore quelque chose aux yeux du suivant." },
          { type: 'paragraph', text: "Aucun des deux ne transforme votre carte en application. Ça marche parce que le client est déjà là, avec le téléphone qui lui a servi à commander." },
        ],
      },
      {
        number: 11,
        heading: "Un seul code, plusieurs langues",
        anchor: 'menu-multilingue',
        content: [
          { type: 'paragraph', text: "Une carte imprimée choisit une langue, ou se dédouble en versions qu'il faut ensuite réimprimer ensemble. Une carte digitale n'a pas ce problème." },
          { type: 'paragraph', text: "ScanQur publie en sept langues (anglais, français, arabe, espagnol, portugais, allemand, italien), avec une vraie prise en charge de l'écriture de droite à gauche pour l'arabe, et non du texte arabe versé dans une mise en page pensée dans l'autre sens. Le client change d'un geste, les traductions peuvent être générées pour vous, et toutes les versions restent alignées quand vous modifiez un prix." },
          { type: 'paragraph', text: "Le gratuit couvre deux langues, le payant lève la limite. Pour un commerce en zone touristique, ou dont la rue ne parle pas une seule langue, c'est en général ce point qui emporte la décision." },
        ],
      },
      {
        number: 12,
        heading: "Statistiques : ce que les clients regardent",
        anchor: 'statistiques-menu',
        content: [
          { type: 'paragraph', text: "Une carte papier ne dit rien de la façon dont elle est lue. Vous savez ce qui s'est vendu. Vous ne savez pas ce qui a été envisagé puis écarté." },
          { type: 'lead', text: "Un menu digital rend compte de lui-même :" },
          {
            type: 'list',
            items: [
              "Les vues : à quelle fréquence le menu est ouvert, et à quelles heures",
              "Les visiteurs uniques, pour distinguer le passage de la consultation répétée",
              "Les produits les plus regardés",
              "Les catégories qui concentrent l'attention",
            ],
          },
          { type: 'paragraph', text: "Chaque chiffre est présenté par rapport à la période précédente : vous lisez une tendance, pas une valeur brute." },
          { type: 'paragraph', text: "L'utile vient de la mise en regard avec votre caisse. Un plat très consulté et peu commandé a le plus souvent un problème de prix, ou de photo, ou de description. Rarement de recette. Une catégorie que personne n'ouvre est peut-être simplement enterrée trop bas dans la page." },
          { type: 'paragraph', text: "Deux règles évitent les fausses conclusions. Jugez des tendances, pas des journées. Et laissez du temps : deux semaines normales avant qu'un motif signifie quelque chose, un mois avant d'agir sur une comparaison. L'historique va à trente jours sur le gratuit, un an sur le payant." },
        ],
      },
      {
        number: 13,
        heading: "À quels commerces cela s'adresse",
        anchor: 'types-de-commerces',
        content: [
          { type: 'paragraph', text: "Les cas évidents sont ceux de la restauration, et il existe des configurations dédiées : restaurants, cafés, boulangeries, bars, restauration rapide, pizzerias." },
          { type: 'lead', text: "Les moins évidents fonctionnent pareil, parce qu'un menu et un catalogue sont le même objet :" },
          {
            type: 'list',
            items: [
              "Épiceries et boucheries qui publient une liste de prix qui bouge chaque semaine",
              "Pharmacies qui présentent ce qu'elles ont en stock",
              "Instituts de beauté qui affichent prestations et durées",
              "Boutiques de prêt-à-porter ou d'électronique avec un catalogue consultable en magasin",
            ],
          },
          { type: 'paragraph', text: "Leur point commun : une liste de références et des prix qui bougent plus souvent qu'il n'est pratique de les réimprimer." },
        ],
      },
      {
        number: 14,
        heading: "Ce que cela change au quotidien",
        anchor: 'benefices-quotidien',
        content: [
          { type: 'lead', text: "Une fois la liste des fonctionnalités mise de côté, voilà ce qu'un commerçant y gagne :" },
          {
            type: 'list',
            items: [
              "Moins de dépendance aux menus papier, et à la facture qui va avec",
              "Des changements de prix et de produits qui prennent une minute au lieu d'un tirage",
              "Une expérience actuelle pour les clients, sans rien leur demander d'installer",
              "Un menu qui s'ouvre tout de suite sur le téléphone qu'ils ont déjà en main",
              "Des produits présentés avec des photos et une mise en page à l'image du commerce",
              "Plusieurs langues derrière un seul code, alignées automatiquement",
              "Des commandes et des réservations qui arrivent structurées plutôt que criées à travers la salle",
              "Horaires, adresse, téléphone et réseaux sur la page que les clients ouvrent déjà",
              "Un lien unique à diffuser partout où vous existez en ligne",
              "Une carte de fidélité qui fait revenir les clients, sans rien à installer de leur côté",
              "Des notes et des avis recueillis là où les gens sont déjà, sur la carte elle-même",
              "Du temps récupéré, parce que la carte cesse d'être une tâche pour devenir un réglage",
            ],
          },
        ],
      },
    ],
    tips: {
      heading: "Réussir le passage au menu digital",
      intro: "La plateforme est la partie facile. Ce qui sépare un menu réellement utilisé d'un menu scanné une fois tient plutôt à la mise en place autour.",
      items: [
        "Partez de la carte réelle, pas de la carte idéale. Publiez ce que vous servez cette semaine",
        "Photographiez d'abord les plats qui se vendent ; quelques bonnes photos valent mieux que quarante médiocres",
        "Placez le code là où la main se pose : chevalet de table, bord de comptoir, vitrine à hauteur des yeux",
        "Ajoutez une consigne courte sur le cadre, parce qu'un code nu n'indique pas ce qu'il ouvre",
        "Tenez les disponibilités à jour les deux premières semaines, le temps que le geste devienne un réflexe",
        "Diffusez aussi le lien du menu en ligne, pour que le code ne soit pas la seule porte d'entrée",
      ],
      outro: "Une fois ces points en place, la carte s'entretient toute seule, dans le temps que vous auriez passé à expliquer un plat épuisé.",
    },
    faq: [
      { q: "Faut-il une application pour ouvrir un menu QR code ?", a: "Non. Les appareils photo des téléphones lisent nativement les QR codes, et le code ouvre une page web classique dans le navigateur. Il n'y a rien à installer, ni côté client ni côté commerce." },
      { q: "Le QR code change-t-il quand je modifie ma carte ?", a: "Non. Le code pointe vers une adresse permanente, pas vers une version du menu. Vous réimprimerez peut-être un code parce qu'il est abîmé. Jamais parce que vos prix ont changé." },
      { q: "ScanQur est-il vraiment gratuit ?", a: "Le produit de base est gratuit, sans carte bancaire ni limite de durée, et les produits comme les catégories y sont illimités. L'offre payante ajoute les langues illimitées, plus de membres d'équipe, un domaine personnalisé, la gestion des commandes et des réservations depuis le tableau de bord, et un historique de statistiques plus long." },
      { q: "Combien de langues un menu peut-il proposer ?", a: "Sept : anglais, français, arabe, espagnol, portugais, allemand et italien, avec un vrai affichage de droite à gauche pour l'arabe. L'offre gratuite en couvre deux à la fois, l'offre payante lève la limite." },
      { q: "Peut-on prendre des commandes sans logiciel de caisse ?", a: "Oui. Les commandes arrivent par WhatsApp sous forme de message structuré et prérempli, ou par les QR codes de table dans un flux de tableau de bord. Aucune commission n'est prélevée et votre mode d'encaissement reste inchangé." },
      { q: "Les clients doivent-ils créer un compte pour commander, laisser un avis ou cumuler des points ?", a: "Non. Passer commande, en suivre l'état ensuite, laisser un avis et remplir une carte de fidélité se font depuis la page du menu, sans application, sans compte et sans collecte de numéro de téléphone." },
      { q: "Est-ce adapté à un commerce qui n'est pas un restaurant ?", a: "Oui. Un catalogue se comporte comme un menu : épiceries, boucheries, pharmacies, salons, prêt-à-porter et électronique utilisent les mêmes catégories, produits et prix, en désactivant la commande s'ils veulent seulement une liste de prix consultable." },
    ],
    conclusion: [
      "Un menu digital ne change pas la façon dont vous tenez votre commerce. Il déplace une chose précise, votre liste de prix, du papier (où la modifier coûte de l'argent et du délai) vers une page que vous éditez depuis votre téléphone entre deux services.",
      "Ce qui en sort est banal et utile. Des prix justes, des plats épuisés signalés en quelques secondes, plusieurs langues derrière un seul code, des commandes lisibles, et une première idée de ce que les gens lisent avant de choisir.",
      {
        parts: [
          { text: "Si votre carte doit bientôt repartir à l'impression, c'est le moment d'essayer autre chose. Créer un " },
          { text: 'menu QR code pour restaurant ou commerce', href: 'https://scanqur.com/fr' },
          { text: " et imprimer son code ne coûte rien, et le pire scénario est de vous retrouver avec un lien à jour à donner à côté de la carte imprimée que vous alliez commander de toute façon." },
        ],
      },
    ],
  },

  // ── Spanish ───────────────────────────────────────────────────────────────
  {
    slug: 'menu-digital-codigo-qr',
    lang: 'es',
    translations: { en: 'qr-code-digital-menu', fr: 'menu-digital-qr-code', es: 'menu-digital-codigo-qr', ar: 'qaimat-raqmiya-bi-ramz-qr' },
    title: 'Menú digital con código QR: cómo ScanQur sustituye la carta de papel',
    seoTitle: 'Menú digital con código QR: crea tu carta con ScanQur',
    seoDescription:
      'Cómo funciona un menú digital con código QR y cómo ScanQur permite a restaurantes y comercios cambiar precios en tiempo real y vender en varios idiomas.',
    imageAlt: 'Un código QR a la izquierda que abre un menú digital en la pantalla de un smartphone',
    headerTint: ['rgba(2,132,199,0.28)', 'rgba(3,105,161,0.34)'],
    publishedDate: '2026-09-04',
    readingMinutes: 14,
    relatedCategories: ['food', 'work', 'tech'],
    relatedArticles: ['beneficios-de-la-risa', 'tipos-de-humor', 'historia-del-humor'],
    intro: [
      'Una carta impresa es una foto fija. Es exacta el día que sale de la imprenta y un poco menos exacta cada semana siguiente. Un proveedor sube el precio, un plato desaparece, y la carta de verano llega tarde otra vez.',
      'El menú digital es la solución evidente, y el código QR lleva ya bastante tiempo en las mesas como para que nadie lo lea como una medida sanitaria. Lo que ha cambiado son las herramientas. Ya no hace falta un programador, ni una cuota que solo tiene sentido si tienes cuarenta locales.',
      'Abajo: qué montas, qué ve tu cliente y cuánto cuesta todo esto. Que, para la mayoría de comercios, es nada.',
    ],
    sections: [
      {
        number: 1,
        heading: 'La carta de papel es la parte más lenta del negocio',
        anchor: 'problema-carta-papel',
        content: [
          { type: 'paragraph', text: 'Casi todo en un comercio cambia en una tarde. Mueves una mesa. Cambias de proveedor. Pones un cartel en el escaparate. La carta es la excepción, porque modificarla significa volver a la imprenta.' },
          { type: 'lead', text: 'Una carta impresa te ata a:' },
          {
            type: 'list',
            items: [
              'Precios que van al ritmo de la imprenta y no al de tus proveedores',
              'Una factura de reimpresión cada vez que sube un ingrediente',
              'Correcciones a bolígrafo y pegatinas sobre los precios antiguos',
              'Platos agotados que hay que explicar mesa por mesa',
              'Un solo idioma, para todo el que entre por la puerta',
            ],
          },
          { type: 'paragraph', text: 'Nada de esto es grave. Son unas horas al mes y un gasto de imprenta, dedicados a mantener al día un papel.' },
        ],
      },
      {
        number: 2,
        heading: 'Qué es ScanQur',
        anchor: 'que-es-scanqur',
        content: [
          {
            type: 'rich',
            parts: [
              { text: 'ScanQur construye un menú o un catálogo de productos digital y lo pone detrás de un código QR. Su página en español explica cómo ' },
              { text: 'crear un menú digital con código QR gratis', href: 'https://scanqur.com/es' },
              { text: '. Está hecho para negocios independientes, no para cadenas: restaurantes, cafeterías, panaderías y bares, pero también fruterías, carnicerías, farmacias, salones y cualquier tienda que solo necesite una lista de precios legible en el móvil.' },
            ],
          },
          { type: 'paragraph', text: 'El menú vive en una dirección fija, scanqur.com/shop/tu-negocio, y el código QR apunta ahí. No a una versión de tu carta. A tu carta. Así que puedes editarla toda la semana y el código impreso sigue funcionando.' },
          { type: 'lead', text: 'Dos cosas antes de la lista de funciones:' },
          {
            type: 'list',
            items: [
              'Tus clientes no instalan nada. El código abre una página web en el navegador del móvil',
              'El producto básico es gratuito, y no en el sentido de prueba: no hay tarjeta que introducir ni cuenta atrás. El plan de pago compra idiomas ilimitados, más cuentas para el equipo, un dominio propio y un historial de estadísticas más largo',
            ],
          },
        ],
      },
      {
        number: 3,
        heading: 'Qué hace el cliente',
        anchor: 'experiencia-del-cliente',
        content: [
          { type: 'lead', text: 'Desde el lado del cliente son tres gestos:' },
          {
            type: 'list',
            items: [
              'Apunta la cámara del móvil al código de la mesa, del escaparate o del mostrador',
              'El menú se abre en el navegador, con fotos, descripciones y los precios de hoy',
              'Navega, cambia de idioma si lo necesita y luego simplemente lee o hace un pedido',
            ],
          },
          { type: 'paragraph', text: 'El pedido es opcional. Un menú puede funcionar en modo de solo consulta, que le va bien a una tienda que solo quiere un catálogo, o a un restaurante que prefiere que los pedidos pasen por sus camareros.' },
          { type: 'paragraph', text: 'Y como es una página web corriente, el enlace sirve también lejos de la mesa. Una biografía de Instagram, una ficha de Google Business, o una respuesta de WhatsApp cuando alguien pregunta si hay algo vegetariano.' },
        ],
      },
      {
        number: 4,
        heading: 'Construir el catálogo',
        anchor: 'construir-el-catalogo',
        content: [
          { type: 'paragraph', text: 'Un menú de ScanQur son categorías que contienen productos. Es como ya piensas tu carta de papel.' },
          {
            type: 'list',
            items: [
              'Categorías con tus propias palabras: Entrantes, Principales, Postres, o Panes, Bollería, Tartas',
              'Productos con nombre, precio, descripción y foto',
              'Etiquetas dietéticas y de alérgenos: halal, vegetariano, picante, contiene sésamo',
              'Un interruptor de disponibilidad por producto, para que un plato agotado desaparezca sin borrarlo',
              'Un buscador en el menú público, que empieza a importar pasadas las cincuenta referencias',
            ],
          },
          { type: 'paragraph', text: 'Reescribir una carta existente es la tarea que todo el mundo aplaza, así que hay un escáner para eso. Fotografías la carta impresa y saca las categorías, los platos y los precios. Tú revisas su trabajo en vez de teclear. Un segundo asistente redacta una descripción de producto, o una publicación para redes, si escribir cuarenta a mano no te apetece. Tú lo lees antes de que salga.' },
          { type: 'paragraph', text: 'También hay una puntuación de calidad que te relee la carta antes de publicar y señala los huecos: una categoría sin precios, productos sin foto. No es gran cosa, pero evita que la primera versión que ven tus clientes sea la de a medias.' },
          { type: 'paragraph', text: 'Los productos y las categorías son ilimitados en todos los planes, gratuito incluido. Lo que compra el plan de pago es capacidad alrededor del menú (idiomas, equipo, historial), no tamaño de carta.' },
        ],
      },
      {
        number: 5,
        heading: 'Cambiar precios y productos en tiempo real',
        anchor: 'actualizaciones-en-tiempo-real',
        content: [
          { type: 'paragraph', text: 'Esta es la parte que devuelve el trabajo inicial, y lo que separa un menú digital de verdad de un PDF de la carta de papel.' },
          {
            type: 'list',
            items: [
              'Un precio modificado está en línea en el siguiente escaneo',
              'Un plato marcado como agotado durante el servicio vuelve mañana',
              'La carta de temporada sale el día que empieza, no la semana en que llega de imprenta',
              'Las categorías se reordenan para que lo que quieres vender se vea primero',
            ],
          },
          { type: 'paragraph', text: 'El código QR no cambia nunca. Imprímelo en un caballete de mesa, un adhesivo de escaparate, el reverso de una tarjeta: sigue valiendo durante toda la vida del menú que abre. Merece la pena decir la diferencia: un generador de códigos QR te da un código que apunta a un archivo, mientras que una plataforma de menús te da uno que apunta a algo que sigues editando.' },
        ],
      },
      {
        number: 6,
        heading: 'Que el menú se parezca al negocio',
        anchor: 'diseno-y-plantillas',
        content: [
          { type: 'paragraph', text: 'Un menú digital con aspecto de hoja de cálculo deshace todo lo que el local intenta decir. ScanQur lo resuelve con plantillas en lugar de una herramienta de diseño, así que sigue siendo una decisión de cinco minutos.' },
          {
            type: 'list',
            items: [
              'Una plantilla que fija la maquetación y la tipografía',
              'Tu logotipo en la cabecera del menú público',
              'Colores y fuentes ajustados a tu rótulo',
              'Una imagen de cabecera, normalmente una foto del local o de un plato insignia',
            ],
          },
          { type: 'paragraph', text: 'Las fotos valen la hora que cuestan. Un menú ilustrado no se lee como una lista de nombres y precios: la gente se entretiene en él, y los platos que nadie logra imaginarse dejan de ser invisibles.' },
        ],
      },
      {
        number: 7,
        heading: 'Códigos QR presentables sobre una mesa',
        anchor: 'codigos-qr-personalizables',
        content: [
          { type: 'paragraph', text: 'El código es señalización. ScanQur lo trata como tal, no como un cuadrado en blanco y negro que descargas una vez y olvidas.' },
          {
            type: 'list',
            items: [
              'Tu logotipo en el centro del código',
              'Tus colores en lugar del negro por defecto',
              'Un marco con una instrucción breve, para que el cliente sepa qué abre',
              'Un código por mesa, cada uno con su etiqueta de mesa impresa',
              'Un PDF listo para imprimir con todos los códigos de mesa',
              'Una descarga en alta definición para una imprenta o un rotulista',
            ],
          },
          { type: 'paragraph', text: 'Los códigos por mesa son los que hacen posible el pedido en mesa: el código es lo que permite que un pedido sepa de dónde viene.' },
          { type: 'paragraph', text: 'Y si pasar por la imprenta no es en lo que quieres gastar la semana, puedes pedir los caballetes, adhesivos y marcos ya impresos, a socios verificados, desde el propio panel.' },
        ],
      },
      {
        number: 8,
        heading: 'Recoger pedidos: WhatsApp, mesas y mostrador',
        anchor: 'pedidos',
        content: [
          { type: 'paragraph', text: 'El pedido es el momento en que el menú deja de ser un documento y pasa a formar parte del servicio. Son canales que activas, y nunca acabas metido en una pasarela de pago que no habías pedido.' },
          { type: 'lead', text: 'El pedido por WhatsApp va así:' },
          {
            type: 'list',
            items: [
              'El cliente recorre el menú y añade artículos a un carrito',
              'Pulsa el botón de WhatsApp y su aplicación se abre con un mensaje ya redactado: cada artículo, la cantidad y el total',
              'Puede añadir una nota y enviarlo a tu número profesional',
              'Recibes un pedido estructurado dentro de una conversación en la que puedes responder, y confirmas la hora de recogida o la forma de pago en el mismo hilo',
            ],
          },
          { type: 'paragraph', text: 'Sin comisión en nada de esto, y tu caja se queda exactamente como está. El canal encaja con todo lo que se prepara para mostrador, recogida o un reparto que llevas tú: comida para llevar, encargos de panadería, una cesta apartada para la tarde, un catering que trabaja con un día de antelación.' },
          { type: 'paragraph', text: 'El pedido en mesa es la versión de sala. Cada mesa tiene su código, así que el pedido llega a tu panel ya asociado al número de mesa y el servicio sigue.' },
          { type: 'paragraph', text: 'El cliente tampoco se queda a ciegas. Una vez enviado el pedido puede seguir su estado en la misma página, sin cuenta y sin instalar nada.' },
          { type: 'lead', text: 'En la parte de gestión:' },
          {
            type: 'list',
            items: [
              'Un flujo en directo de los pedidos que entran',
              'Cierre o cancelación con un motivo registrado',
              'Un historial que puedes consultar',
              'Un informe mensual de facturación, ticket medio y tasa de cancelación',
            ],
          },
          { type: 'paragraph', text: 'Nada de esto tiene que caer en el móvil de una sola persona. Invitas al equipo con roles, encargado, cocina, camarero, recepción o sala, y cada uno ve solo la parte del panel que su puesto necesita. Es también como se sale de la contraseña compartida en la que acaban casi todos los equipos pequeños.' },
        ],
      },
      {
        number: 9,
        heading: 'Reservas, y las preguntas por las que te llaman',
        anchor: 'reservas-e-informacion',
        content: [
          { type: 'paragraph', text: 'Quien lee la carta esta noche suele ser quien reservará mesa la semana que viene. ScanQur deja que lo haga en la página que ya tiene delante, sin mandarlo a una segunda plataforma y sin comisión por cada comensal.' },
          { type: 'paragraph', text: 'Las reservas funcionan como formulario de autoservicio o en un modo solo teléfono, si prefieres mantener esa conversación por línea. En los dos casos te llega un correo en cuanto se ocupa una mesa.' },
          { type: 'lead', text: 'El menú público lleva además aquello por lo que te telefonean:' },
          {
            type: 'list',
            items: [
              'Nombre del negocio, logotipo y una descripción breve',
              'Dirección con enlace al mapa',
              'Teléfono, WhatsApp y correo electrónico',
              'Horarios, con un indicador de si estás abierto ahora',
              'Enlaces a tus redes: Instagram, Facebook, TikTok, YouTube',
            ],
          },
          { type: 'paragraph', text: 'Absorbe en silencio muchas interrupciones en pleno servicio, y convierte al menú en el único enlace que merece la pena difundir allí donde apareces en internet.' },
        ],
      },
      {
        number: 10,
        heading: 'Convertir a un cliente de paso en habitual',
        anchor: 'fidelidad-y-resenas',
        content: [
          { type: 'paragraph', text: 'La carta ya es el sitio donde ocurre la relación con el cliente, así que ahí viven dos cosas que normalmente piden sus propias herramientas.' },
          { type: 'lead', text: 'Primero, la tarjeta de sellos:' },
          {
            type: 'list',
            items: [
              'Los sellos salen de pedidos que ya marcas como completados, así que no hay nada extra que gestionar',
              'El cliente los acumula en su propio móvil, sin aplicación ni cuenta',
              'Funciona sin recoger ningún teléfono ni correo',
              'Tras unas cuantas visitas reclama una recompensa gratuita',
            ],
          },
          { type: 'paragraph', text: 'Después las reseñas. Los clientes te valoran en la propia carta, donde ya estaban mirando, en lugar de en una plataforma que tendrían que ir a buscar. Una reseña que consideres injusta se reporta pero no se borra, y esa es la única razón por la que la nota significa algo para quien la lea después.' },
          { type: 'paragraph', text: 'Ninguna de las dos convierte tu carta en una aplicación. Funcionan porque el cliente ya está ahí, con el móvil con el que acaba de pedir.' },
        ],
      },
      {
        number: 11,
        heading: 'Un solo código, varios idiomas',
        anchor: 'menu-multilingue',
        content: [
          { type: 'paragraph', text: 'Una carta impresa elige un idioma, o se desdobla en versiones que luego hay que reimprimir juntas. Una carta digital no tiene ese problema.' },
          { type: 'paragraph', text: 'ScanQur publica en siete idiomas (inglés, francés, árabe, español, portugués, alemán, italiano), con soporte real de derecha a izquierda para el árabe y no texto árabe volcado en una maquetación pensada al revés. El cliente cambia con un gesto, las traducciones pueden generarse por ti, y todas las versiones siguen alineadas cuando modificas un precio.' },
          { type: 'paragraph', text: 'El gratuito cubre dos idiomas, el de pago levanta el límite. Para un negocio en zona turística, o cuya calle no habla un solo idioma, esto suele ser lo que decide.' },
        ],
      },
      {
        number: 12,
        heading: 'Estadísticas: qué miran los clientes',
        anchor: 'estadisticas-del-menu',
        content: [
          { type: 'paragraph', text: 'Una carta de papel no dice nada sobre cómo se lee. Sabes lo que se vendió. No sabes lo que se consideró y se descartó.' },
          { type: 'lead', text: 'Un menú digital informa sobre sí mismo:' },
          {
            type: 'list',
            items: [
              'Visitas: con qué frecuencia se abre el menú y a qué horas',
              'Visitantes únicos, para distinguir el paso de la consulta repetida',
              'Los productos más mirados',
              'Las categorías donde se concentra la atención',
            ],
          },
          { type: 'paragraph', text: 'Cada dato se muestra frente al periodo anterior, así que lees una dirección y no una cifra suelta.' },
          { type: 'paragraph', text: 'Lo útil sale de poner esos números junto a tu caja. Un plato muy visto y poco pedido suele tener un problema de precio, o de foto, o de descripción. Rara vez de receta. Una categoría que nadie abre quizá esté simplemente enterrada demasiado abajo en la página.' },
          { type: 'paragraph', text: 'Dos reglas evitan conclusiones falsas. Juzga tendencias, no días sueltos. Y dale tiempo: dos semanas normales antes de que un patrón signifique algo, un mes antes de actuar sobre una comparación. El historial llega a treinta días en el gratuito y a un año en el de pago.' },
        ],
      },
      {
        number: 13,
        heading: 'A qué negocios les encaja',
        anchor: 'tipos-de-negocio',
        content: [
          { type: 'paragraph', text: 'Los casos evidentes son los de hostelería, y hay configuraciones específicas para ellos: restaurantes, cafeterías, panaderías, bares, comida rápida, pizzerías.' },
          { type: 'lead', text: 'Los menos evidentes funcionan igual, porque un menú y un catálogo son el mismo objeto:' },
          {
            type: 'list',
            items: [
              'Tiendas de alimentación y carnicerías que publican una lista de precios que se mueve cada semana',
              'Farmacias que muestran lo que tienen en stock',
              'Centros de estética que enseñan servicios y duraciones',
              'Tiendas de ropa o de electrónica con un catálogo consultable en el propio local',
            ],
          },
          { type: 'paragraph', text: 'Lo que tienen en común es una lista de referencias con precios que se mueven más a menudo de lo que resulta cómodo reimprimirlos.' },
        ],
      },
      {
        number: 14,
        heading: 'Qué cambia en el día a día',
        anchor: 'beneficios-diarios',
        content: [
          { type: 'lead', text: 'Dejando a un lado la lista de funciones, esto es lo que gana un comerciante:' },
          {
            type: 'list',
            items: [
              'Menos dependencia de los menús de papel y de la factura que llevan detrás',
              'Cambios de precio y de producto que llevan un minuto en vez de una tirada',
              'Una experiencia actual para el cliente, sin pedirle que instale nada',
              'Un menú que se abre al instante en el móvil que ya tiene en la mano',
              'Productos presentados con fotos y una maquetación a la altura del negocio',
              'Varios idiomas detrás de un solo código, alineados automáticamente',
              'Pedidos y reservas que llegan estructurados en lugar de gritados por la sala',
              'Horarios, dirección, teléfono y redes en la página que el cliente ya abre',
              'Un único enlace que merece la pena difundir allí donde existes en internet',
              'Una tarjeta de sellos que hace volver a los clientes, sin nada que instalar por su parte',
              'Valoraciones y reseñas recogidas donde la gente ya está, en la propia carta',
              'Tiempo recuperado, porque la carta deja de ser una tarea y pasa a ser un ajuste',
            ],
          },
        ],
      },
    ],
    tips: {
      heading: 'Acertar con el cambio al menú digital',
      intro: 'La plataforma es la parte fácil. Lo que separa un menú que se usa de uno que se escanea una sola vez tiene más que ver con la puesta en marcha alrededor.',
      items: [
        'Parte de la carta real, no de la ideal. Publica lo que sirves esta semana',
        'Fotografía primero los platos que se venden; unas pocas fotos buenas valen más que cuarenta mediocres',
        'Coloca el código donde la mano se apoya: caballete de mesa, borde del mostrador, escaparate a la altura de los ojos',
        'Añade una instrucción breve en el marco, porque un código desnudo no dice qué abre',
        'Mantén las disponibilidades al día las dos primeras semanas, hasta que el gesto sea un reflejo',
        'Difunde también el enlace del menú en internet, para que el código no sea la única puerta de entrada',
      ],
      outro: 'Con eso resuelto, la carta se mantiene sola en el mismo rato que habrías dedicado a explicar un plato agotado.',
    },
    faq: [
      { q: '¿Hace falta una aplicación para abrir un menú QR?', a: 'No. Las cámaras de los móviles leen códigos QR de forma nativa y el código abre una página web normal en el navegador. No hay nada que instalar, ni en el lado del cliente ni en el del negocio.' },
      { q: '¿Cambia el código QR cuando modifico la carta?', a: 'No. El código apunta a una dirección permanente, no a una versión del menú. Quizá reimprimas un código porque se ha estropeado. Nunca porque hayan cambiado tus precios.' },
      { q: '¿ScanQur es realmente gratis?', a: 'El producto básico es gratuito, sin tarjeta ni límite de tiempo, y en él los productos y las categorías son ilimitados. El plan de pago añade idiomas ilimitados, más miembros del equipo, dominio propio, gestión de pedidos y reservas desde el panel y un historial de estadísticas más largo.' },
      { q: '¿Cuántos idiomas puede tener un menú?', a: 'Siete: inglés, francés, árabe, español, portugués, alemán e italiano, con representación real de derecha a izquierda para el árabe. El plan gratuito cubre dos a la vez; el de pago levanta el límite.' },
      { q: '¿Se pueden recoger pedidos sin un TPV?', a: 'Sí. Los pedidos llegan por WhatsApp como un mensaje estructurado y ya redactado, o por los códigos QR de mesa a un flujo en el panel. No se cobra comisión y tu forma de cobro actual se queda como está.' },
      { q: '¿Necesita el cliente una cuenta para pedir, reseñar o acumular sellos?', a: 'No. Hacer un pedido, seguir su estado después, dejar una reseña y rellenar una tarjeta de fidelidad ocurren en la propia página del menú, sin aplicación, sin cuenta y sin recoger ningún número de teléfono.' },
      { q: '¿Sirve para un comercio que no es un restaurante?', a: 'Sí. Un catálogo se comporta como un menú: tiendas de alimentación, carnicerías, farmacias, salones, ropa y electrónica usan las mismas categorías, productos y precios, y desactivan el pedido si solo quieren una lista de precios consultable.' },
    ],
    conclusion: [
      'Un menú digital no cambia cómo llevas tu negocio. Mueve una cosa concreta, tu lista de precios, del papel (donde cambiarla cuesta dinero y espera) a una página que editas desde el móvil entre dos servicios.',
      'Lo que sale de ahí es corriente y útil. Precios que están bien, platos agotados señalados en segundos, varios idiomas detrás de un solo código, pedidos legibles, y una primera idea de lo que la gente lee antes de elegir.',
      {
        parts: [
          { text: 'Si a tu carta le toca pronto volver a imprenta, ese es el momento de probar otra cosa. Crear un ' },
          { text: 'menú con código QR para restaurantes y comercios', href: 'https://scanqur.com/es' },
          { text: ' e imprimir su código no cuesta nada, y en el peor de los casos acabas con un enlace actualizado que dar junto a la carta impresa que ibas a encargar de todos modos.' },
        ],
      },
    ],
  },

  // ── Arabic ────────────────────────────────────────────────────────────────
  {
    slug: 'qaimat-raqmiya-bi-ramz-qr',
    lang: 'ar',
    translations: { en: 'qr-code-digital-menu', fr: 'menu-digital-qr-code', es: 'menu-digital-codigo-qr', ar: 'qaimat-raqmiya-bi-ramz-qr' },
    title: 'قائمة رقمية برمز QR: كيف تُغني ScanQur عن القائمة الورقية',
    seoTitle: 'قائمة طعام رقمية برمز QR: أنشئ قائمتك مع ScanQur',
    seoDescription:
      'كيف تعمل القائمة الرقمية برمز QR، وكيف تتيح ScanQur للمطاعم والمحلات تعديل الأسعار لحظياً وعرض قائمتهم بعدة لغات.',
    imageAlt: 'رمز QR على اليسار يفتح قائمة طعام رقمية معروضة على شاشة هاتف ذكي',
    headerTint: ['rgba(2,132,199,0.28)', 'rgba(3,105,161,0.34)'],
    publishedDate: '2026-09-04',
    readingMinutes: 14,
    relatedCategories: ['food', 'work', 'tech'],
    relatedArticles: ['fawaid-al-dahk', 'anwaa-al-fukaha', 'tarikh-al-fukaha'],
    intro: [
      'القائمة المطبوعة صورة ثابتة للحظة واحدة. تكون صحيحة يوم خروجها من المطبعة، وأقل صحةً قليلاً كل أسبوع بعدها. مورّد يرفع سعره، وطبق يخرج من القائمة، وقائمة الصيف تتأخر مرة أخرى.',
      'القائمة الرقمية هي الحل البديهي، ورمز QR جالس على الطاولات منذ وقت يكفي لألا يقرأه أحد اليوم كإجراء صحي. الذي تغيّر هو الأدوات. لم تعد تحتاج مبرمجاً، ولا اشتراكاً لا معنى له إلا إن كنت تملك أربعين مطعماً.',
      'في ما يلي: ما الذي تبنيه، وما الذي يراه زبونك، وكم يكلّف هذا كله. وهو، عند أغلب التجار، لا شيء.',
    ],
    sections: [
      {
        number: 1,
        heading: 'القائمة الورقية هي أبطأ جزء في المحل',
        anchor: 'mushkilat-al-qaima-al-waraqiya',
        content: [
          { type: 'paragraph', text: 'أغلب ما في المحل يتغيّر خلال بعد ظهر واحد. تنقل طاولة. تبدّل مورّداً. تعلّق لافتة في الواجهة. القائمة هي الاستثناء، لأن تعديلها يعني العودة إلى المطبعة.' },
          { type: 'lead', text: 'القائمة المطبوعة تقيّدك بـ:' },
          {
            type: 'list',
            items: [
              'أسعار تسير بإيقاع المطبعة لا بإيقاع مورّديك',
              'فاتورة إعادة طباعة كلما ارتفع سعر مكوّن',
              'تصحيحات بالقلم وملصقات فوق الأسعار القديمة',
              'أطباق نفدت تشرحها طاولةً طاولة',
              'لغة واحدة، لكل من يدخل الباب',
            ],
          },
          { type: 'paragraph', text: 'لا شيء من هذا جسيم. بضع ساعات شهرياً وميزانية طباعة، تُنفق على إبقاء ورقة مطابقة للواقع.' },
        ],
      },
      {
        number: 2,
        heading: 'ما هي ScanQur',
        anchor: 'ma-hiya-scanqur',
        content: [
          {
            type: 'rich',
            parts: [
              { text: 'تبني ScanQur قائمة طعام أو كتالوج منتجات رقمياً وتضعه خلف رمز QR. وتشرح صفحتها العربية كيفية ' },
              { text: 'إنشاء قائمة طعام رقمية برمز QR مجاناً', href: 'https://scanqur.com/ar' },
              { text: '. صُنعت للمحال المستقلة لا للسلاسل: مطاعم ومقاهٍ ومخابز وحانات، وكذلك بقالات وجزارات وصيدليات وصالونات، وأي متجر يحتاج فقط قائمة أسعار يقرأها الناس على هواتفهم.' },
            ],
          },
          { type: 'paragraph', text: 'تعيش القائمة على عنوان ثابت، scanqur.com/shop/اسم-محلك، ورمز QR يشير إلى هناك. لا إلى نسخة من قائمتك. بل إلى قائمتك. فتعدّلها طوال الأسبوع، ويظل الرمز المطبوع يعمل.' },
          { type: 'lead', text: 'أمران يستحقان المعرفة من البداية:' },
          {
            type: 'list',
            items: [
              'زبائنك لا يثبّتون شيئاً. الرمز يفتح صفحة ويب في متصفح الهاتف',
              'المنتج الأساسي مجاني، وليس بمعنى التجربة: لا بطاقة بنكية تُدخلها ولا عدّاد يعمل. والخطة المدفوعة تشتري لغات غير محدودة وحسابات أكثر للفريق ونطاقاً خاصاً وسجل إحصاءات أطول',
            ],
          },
        ],
      },
      {
        number: 3,
        heading: 'ماذا يفعل الزبون',
        anchor: 'tajribat-az-zabun',
        content: [
          { type: 'lead', text: 'من جهة الزبون، الأمر ثلاث حركات:' },
          {
            type: 'list',
            items: [
              'يوجّه كاميرا هاتفه إلى الرمز، على الطاولة أو في الواجهة أو عند الكاونتر',
              'تُفتح القائمة في متصفحه، بالصور والأوصاف وأسعار اليوم',
              'يتصفّح، ويبدّل اللغة إن احتاج، ثم يكتفي بالقراءة أو يرسل طلباً',
            ],
          },
          { type: 'paragraph', text: 'الطلب اختياري. يمكن للقائمة أن تعمل بوضع العرض فقط، وهو ما يناسب متجراً يريد كتالوجاً لا أكثر، أو مطعماً يفضّل أن تمر الطلبات عبر نُدُله.' },
          { type: 'paragraph', text: 'ولأنها صفحة ويب عادية، ينفع الرابط بعيداً عن الطاولة أيضاً. نبذة إنستغرام، أو بطاقة Google Business، أو رد على واتساب حين يسأل أحدهم إن كان لديك شيء نباتي.' },
        ],
      },
      {
        number: 4,
        heading: 'بناء الكتالوج',
        anchor: 'bina-al-katalog',
        content: [
          { type: 'paragraph', text: 'قائمة ScanQur هي فئات تحتوي منتجات. وهكذا تفكّر أصلاً في قائمتك الورقية.' },
          {
            type: 'list',
            items: [
              'فئات بكلماتك أنت: مقبلات، أطباق رئيسية، حلويات، أو خبز ومعجنات وكعك',
              'منتجات باسم وسعر ووصف وصورة',
              'إشارات غذائية وتحسسية: حلال، نباتي، حار، يحتوي على السمسم',
              'مفتاح توافر لكل منتج، ليختفي الطبق الناقص دون حذفه',
              'خانة بحث في القائمة العلنية، تبدأ أهميتها بعد خمسين صنفاً',
            ],
          },
          { type: 'paragraph', text: 'إعادة كتابة قائمة موجودة هي المهمة التي يؤجلها الجميع، ولهذا يوجد ماسح لها. تصوّر القائمة المطبوعة فيستخرج الفئات والأطباق والأسعار. أنت تراجع عمله بدل أن تكتب. ومساعد ثانٍ يصوغ وصف منتج، أو منشوراً للشبكات الاجتماعية، إن لم تكن كتابة أربعين وصفاً تغريك. وأنت تقرأه قبل أن يخرج.' },
          { type: 'paragraph', text: 'وهناك أيضاً مؤشر جودة يقرأ القائمة عليك قبل النشر ويشير إلى الثغرات: فئة بلا أسعار، منتجات بلا صور. ليست ميزة كبيرة، لكنها تمنع أن تكون النسخة الأولى التي يراها زبائنك هي نصف المكتملة.' },
          { type: 'paragraph', text: 'المنتجات والفئات غير محدودة في كل الخطط، والمجانية منها. ما تشتريه الخطة المدفوعة هو سعة حول القائمة (لغات، فريق، سجل)، لا حجم قائمة.' },
        ],
      },
      {
        number: 5,
        heading: 'تعديل الأسعار والمنتجات لحظياً',
        anchor: 'tahdith-lahzi',
        content: [
          { type: 'paragraph', text: 'هذا هو الجزء الذي يردّ لك جهد الإعداد، وما يفصل قائمة رقمية حقيقية عن ملف PDF للقائمة الورقية.' },
          {
            type: 'list',
            items: [
              'السعر المعدّل يظهر مع أول مسح تالٍ',
              'الطبق الذي تعلّمه ناقصاً هذه الأمسية يعود غداً',
              'قائمة الموسم تصدر يوم بدايته، لا في أسبوع تسليم المطبعة',
              'ترتيب الفئات يتغيّر ليقع ما تريد بيعه أول ما تقع عليه العين',
            ],
          },
          { type: 'paragraph', text: 'أما رمز QR فلا يتحرك أبداً. اطبعه على حامل طاولة أو ملصق واجهة أو ظهر بطاقة عمل، ويبقى صالحاً طوال عمر القائمة التي يفتحها. والفرق يستحق أن يُقال: مولّد رموز QR يعطيك رمزاً يشير إلى ملف، بينما تعطيك منصة القوائم رمزاً يشير إلى شيء تظل تحرّره.' },
        ],
      },
      {
        number: 6,
        heading: 'أن تشبه القائمة المحل نفسه',
        anchor: 'at-tasmim-wa-al-qawalib',
        content: [
          { type: 'paragraph', text: 'قائمة رقمية تشبه جدول بيانات تُلغي كل ما تحاول الصالة قوله. تعالج ScanQur ذلك بقوالب جاهزة بدل أداة تصميم، فيبقى القرار في حدود خمس دقائق.' },
          {
            type: 'list',
            items: [
              'قالب يحدد التنسيق والخطوط',
              'شعارك في أعلى القائمة العلنية',
              'ألوان وخطوط مضبوطة على لافتة محلك',
              'صورة رأسية، غالباً صورة للصالة أو لطبق مميز',
            ],
          },
          { type: 'paragraph', text: 'الصور تستحق الساعة التي تكلفها. القائمة المصوّرة لا تُقرأ كلائحة أسماء وأسعار: يتمهّل الزبائن عندها، والأطباق التي يصعب تخيّلها تكفّ عن كونها غير مرئية.' },
        ],
      },
      {
        number: 7,
        heading: 'رموز QR تليق بأن توضع على طاولة',
        anchor: 'rumuz-qr-mukhassasa',
        content: [
          { type: 'paragraph', text: 'الرمز لافتة. وتتعامل معه ScanQur على هذا الأساس، لا كمربع أبيض وأسود تنزّله مرة ثم تنساه.' },
          {
            type: 'list',
            items: [
              'شعارك في وسط الرمز',
              'ألوانك بدل الأسود الافتراضي',
              'إطار يحمل تعليمة قصيرة، ليعرف الزبون ما الذي يفتحه',
              'رمز لكل طاولة، مع لصاقة رقم الطاولة مطبوعة',
              'ملف PDF جاهز للطباعة يضم رموز كل الطاولات',
              'تنزيل بدقة عالية لمطبعة أو صانع لافتات',
            ],
          },
          { type: 'paragraph', text: 'ورموز الطاولات هي ما يجعل الطلب من الطاولة ممكناً: الرمز هو ما يتيح للطلب أن يعرف من أين جاء.' },
          { type: 'paragraph', text: 'وإن لم تكن زيارة المطبعة ما ترغب في قضاء أسبوعك فيه، يمكنك طلب حوامل الطاولات والملصقات والإطارات مطبوعةً جاهزة، من شركاء معتمدين، من داخل لوحة التحكم.' },
        ],
      },
      {
        number: 8,
        heading: 'استقبال الطلبات: واتساب والطاولات والكاونتر',
        anchor: 'istiqbal-at-talabat',
        content: [
          { type: 'paragraph', text: 'الطلب هو اللحظة التي تكفّ فيها القائمة عن كونها مستنداً لتصبح جزءاً من الخدمة. هذه قنوات تشغّلها أنت، ولن تجد نفسك يوماً داخل بوابة دفع لم تطلبها.' },
          { type: 'lead', text: 'الطلب عبر واتساب يجري هكذا:' },
          {
            type: 'list',
            items: [
              'يتصفح الزبون القائمة ويضيف أصنافاً إلى سلة',
              'يضغط زر واتساب، فيُفتح تطبيقه على رسالة مكتوبة سلفاً بكل صنف وكميته والمجموع',
              'يمكنه إضافة ملاحظة ثم إرسالها إلى رقم عملك',
              'يصلك طلب منظَّم داخل محادثة يمكنك الرد فيها، فتؤكد موعد الاستلام أو طريقة الدفع في الخيط نفسه',
            ],
          },
          { type: 'paragraph', text: 'لا عمولة على أي من هذا، وصندوقك يبقى كما هو تماماً. تناسب هذه القناة كل ما يُحضَّر للكاونتر أو للاستلام أو لتوصيل تتولاه بنفسك: البيع للخارج، وطلبيات المخبز المسبقة، وسلة بقالة تُترك جانباً لبعد الظهر، ومتعهد طعام يعمل قبل يوم.' },
          { type: 'paragraph', text: 'أما الطلب من الطاولة فهو نسخة الصالة. لكل طاولة رمزها، فيهبط الطلب في لوحة التحكم مقترناً برقم الطاولة، وتستمر الخدمة.' },
          { type: 'paragraph', text: 'ولا يُترك الزبون في حيرة أيضاً. بعد إرساله الطلب يتابع حالته على الصفحة نفسها، بلا حساب وبلا تثبيت أي شيء.' },
          { type: 'lead', text: 'وعلى جانب الإدارة:' },
          {
            type: 'list',
            items: [
              'تدفق مباشر للطلبات الواردة',
              'إتمام أو إلغاء مع تسجيل السبب',
              'سجل يمكن الرجوع إليه',
              'تقرير شهري عن رقم الأعمال ومتوسط الطلب ونسبة الإلغاء',
            ],
          },
          { type: 'paragraph', text: 'ولا شيء من هذا مضطر للنزول على هاتف شخص واحد. تدعو الفريق بأدوار، مدير أو مطبخ أو نادل أو استقبال أو صالة، ولا يرى كل واحد إلا ما يحتاجه عمله من لوحة التحكم. وهكذا أيضاً تخرج من عادة كلمة السر المشتركة التي تنتهي إليها معظم الفرق الصغيرة.' },
        ],
      },
      {
        number: 9,
        heading: 'الحجوزات، والأسئلة التي يتصل بك الناس لأجلها',
        anchor: 'al-hujuzat-wa-al-maalumat',
        content: [
          { type: 'paragraph', text: 'من يقرأ قائمة الليلة هو غالباً من سيحجز طاولة الأسبوع المقبل. وتتيح له ScanQur أن يفعل ذلك على الصفحة التي أمامه أصلاً، دون إرساله إلى منصة ثانية ودون رسوم على كل ضيف.' },
          { type: 'paragraph', text: 'ويعمل الحجز إما بنموذج ذاتي الخدمة، وإما بوضع الهاتف فقط، إن كنت تفضّل إبقاء ذلك الحديث على الخط. وفي الحالتين تصلك رسالة بريد لحظة حجز أي طاولة.' },
          { type: 'lead', text: 'وتحمل القائمة العلنية أيضاً ما يتصل بك الناس لأجله:' },
          {
            type: 'list',
            items: [
              'اسم المحل وشعاره ووصف قصير',
              'العنوان مع رابط إلى الخريطة',
              'الهاتف وواتساب والبريد الإلكتروني',
              'أوقات العمل، مع مؤشر يبيّن إن كنت مفتوحاً الآن',
              'روابط حساباتك: إنستغرام وفيسبوك وتيك توك ويوتيوب',
            ],
          },
          { type: 'paragraph', text: 'وهو يمتص بهدوء كثيراً من المقاطعات في عزّ الخدمة، ويجعل القائمة الرابط الوحيد الجدير بالنشر أينما كنت موجوداً على الإنترنت.' },
        ],
      },
      {
        number: 10,
        heading: 'تحويل زبون عابر إلى زبون دائم',
        anchor: 'al-wala-wa-al-taqyimat',
        content: [
          { type: 'paragraph', text: 'القائمة هي أصلاً المكان الذي تجري فيه العلاقة مع الزبون، ولذلك يسكنها أمران يطلبان عادةً أدواتهما الخاصة.' },
          { type: 'lead', text: 'بطاقة الولاء بالأختام أولاً:' },
          {
            type: 'list',
            items: [
              'الأختام تأتي من طلبات تعلّمها أنت أصلاً كمكتملة، فلا شيء إضافي تديره',
              'يجمعها الزبون على هاتفه هو، بلا تطبيق ولا حساب',
              'وتعمل دون جمع رقم هاتف أو بريد إلكتروني',
              'وبعد بضع زيارات يطالب بمكافأة مجانية',
            ],
          },
          { type: 'paragraph', text: 'ثم التقييمات. يقيّمك الزبائن على القائمة نفسها، حيث كانوا ينظرون أصلاً، بدل منصة عليهم البحث عنها. والرأي الذي تعترض عليه يُبلَّغ عنه ولا يُحذف، وهذا وحده ما يجعل التقييم يعني شيئاً لمن يقرأه بعدك.' },
          { type: 'paragraph', text: 'ولا يحوّل أي منهما قائمتك إلى تطبيق. إنهما يعملان لأن الزبون موجود هناك أصلاً، بالهاتف نفسه الذي طلب به.' },
        ],
      },
      {
        number: 11,
        heading: 'رمز واحد، لغات عدة',
        anchor: 'qaima-mutaaddidat-al-lughat',
        content: [
          { type: 'paragraph', text: 'القائمة المطبوعة تختار لغة، أو تنقسم إلى نسخ يلزم بعدها إعادة طباعتها معاً. والقائمة الرقمية لا تعاني هذا.' },
          { type: 'paragraph', text: 'تنشر ScanQur بسبع لغات (الإنجليزية والفرنسية والعربية والإسبانية والبرتغالية والألمانية والإيطالية)، بدعم حقيقي للكتابة من اليمين إلى اليسار في العربية، لا نصاً عربياً مصبوباً في تصميم مبني في الاتجاه الآخر. يبدّل الزبون بلمسة، ويمكن توليد الترجمات لك، وتبقى كل النسخ متسقة حين تغيّر سعراً.' },
          { type: 'paragraph', text: 'المجانية تغطي لغتين، والمدفوعة ترفع الحد. وبالنسبة لمحل في منطقة سياحية، أو في شارع لا يتكلم أهله لغة واحدة، هذه عادةً هي النقطة التي تحسم.' },
        ],
      },
      {
        number: 12,
        heading: 'الإحصاءات: ما ينظر إليه الزبائن',
        anchor: 'ihsaat-al-qaima',
        content: [
          { type: 'paragraph', text: 'القائمة الورقية لا تخبرك شيئاً عن طريقة قراءتها. أنت تعرف ما بيع. ولا تعرف ما فُكِّر فيه ثم تُرك.' },
          { type: 'lead', text: 'أما القائمة الرقمية فتخبرك عن نفسها:' },
          {
            type: 'list',
            items: [
              'المشاهدات: كم مرة تُفتح القائمة، وفي أي ساعات',
              'الزوار الفريدون، للتمييز بين المرور والتصفح المتكرر',
              'أكثر المنتجات نظراً',
              'الفئات التي يتركز فيها الانتباه',
            ],
          },
          { type: 'paragraph', text: 'ويُعرض كل رقم مقارنةً بالفترة السابقة، فأنت تقرأ اتجاهاً لا قيمة مجردة.' },
          { type: 'paragraph', text: 'والمفيد يأتي من وضع هذه الأرقام إلى جانب صندوقك. الطبق كثير المشاهدة قليل الطلب مشكلته غالباً في السعر، أو في الصورة، أو في الوصف. ونادراً في الوصفة. والفئة التي لا يفتحها أحد ربما دُفنت في أسفل الصفحة فحسب.' },
          { type: 'paragraph', text: 'قاعدتان تقيان من الاستنتاجات الخاطئة. احكم على الاتجاهات لا على الأيام. وامنح الأمر وقتاً: أسبوعان اعتياديان قبل أن يعني نمط شيئاً، وشهر قبل أن تتصرف بناءً على مقارنة. يمتد السجل إلى ثلاثين يوماً في المجانية وإلى سنة في المدفوعة.' },
        ],
      },
      {
        number: 13,
        heading: 'لأي المحال يصلح هذا',
        anchor: 'anwaa-al-mahall',
        content: [
          { type: 'paragraph', text: 'الحالات البديهية هي المطاعم والمقاهي، ولها إعدادات مخصصة: مطاعم ومقاهٍ ومخابز وحانات ووجبات سريعة ومطاعم بيتزا.' },
          { type: 'lead', text: 'والأقل بداهةً تعمل بالطريقة نفسها، لأن القائمة والكتالوج شيء واحد:' },
          {
            type: 'list',
            items: [
              'بقالات وجزارات تنشر قائمة أسعار تتحرك كل أسبوع',
              'صيدليات تعرض ما لديها من مخزون',
              'صالونات تجميل تبيّن الخدمات ومددها',
              'متاجر ألبسة أو إلكترونيات بكتالوج يُتصفَّح داخل المتجر',
            ],
          },
          { type: 'paragraph', text: 'ما يجمعها قائمة أصناف بأسعار تتحرك أكثر مما يريح إعادةُ طباعتها.' },
        ],
      },
      {
        number: 14,
        heading: 'ما الذي يتغير في اليوم العادي',
        anchor: 'al-fawaid-al-yawmiya',
        content: [
          { type: 'lead', text: 'انزع قائمة المزايا، وهذا ما يكسبه التاجر:' },
          {
            type: 'list',
            items: [
              'اعتماد أقل على القوائم الورقية، وعلى الفاتورة التي وراءها',
              'تغييرات في الأسعار والمنتجات تستغرق دقيقة بدل طبعة كاملة',
              'تجربة معاصرة للزبون، دون مطالبته بتثبيت أي شيء',
              'قائمة تُفتح فوراً على الهاتف الذي في يده أصلاً',
              'منتجات معروضة بصور وتنسيق يليق بالمحل',
              'عدة لغات خلف رمز واحد، متسقة تلقائياً',
              'طلبات وحجوزات تصل منظَّمة بدل أن تُنادى عبر الصالة',
              'أوقات العمل والعنوان والهاتف والحسابات على الصفحة التي يفتحها الزبون أصلاً',
              'رابط واحد يستحق النشر أينما كنت موجوداً على الإنترنت',
              'بطاقة أختام تعيد الزبائن، دون أن يثبّتوا شيئاً من جهتهم',
              'تقييمات وآراء تُجمع حيث يوجد الناس أصلاً، على القائمة نفسها',
              'وقت مستعاد، لأن القائمة تكفّ عن كونها مهمة لتصير إعداداً تضبطه',
            ],
          },
        ],
      },
    ],
    tips: {
      heading: 'كيف تنجح في الانتقال إلى القائمة الرقمية',
      intro: 'المنصة هي الجزء السهل. وما يفصل قائمة تُستعمل فعلاً عن قائمة تُمسح مرة واحدة هو غالباً الإعداد المحيط بها.',
      items: [
        'ابدأ من القائمة الحقيقية لا من المثالية. انشر ما تقدّمه هذا الأسبوع',
        'صوّر أولاً الأطباق التي تُباع؛ بضع صور جيدة خير من أربعين رديئة',
        'ضع الرمز حيث تقع اليد: حامل الطاولة، حافة الكاونتر، الواجهة على مستوى النظر',
        'أضف تعليمة قصيرة على الإطار، فالرمز العاري لا يدل على ما يفتحه',
        'حافظ على تحديث التوافر في الأسبوعين الأولين، حتى تصير الحركة تلقائية',
        'انشر رابط القائمة على الإنترنت أيضاً، حتى لا يكون الرمز المدخل الوحيد',
      ],
      outro: 'وبعد ضبط هذه النقاط، تصون القائمة نفسها في الوقت الذي كنت ستقضيه في شرح طبق نفد.',
    },
    faq: [
      { q: 'هل يحتاج الزبون تطبيقاً لفتح قائمة QR؟', a: 'لا. كاميرات الهواتف تقرأ رموز QR أصلاً، والرمز يفتح صفحة ويب عادية في المتصفح. لا شيء يُثبَّت، لا عند الزبون ولا عند المحل.' },
      { q: 'هل يتغير رمز QR حين أعدّل قائمتي؟', a: 'لا. الرمز يشير إلى عنوان دائم لا إلى نسخة من القائمة. قد تعيد طباعة رمز لأنه تلف. لكن ليس لأن أسعارك تغيّرت.' },
      { q: 'هل ScanQur مجانية فعلاً؟', a: 'المنتج الأساسي مجاني، بلا بطاقة بنكية وبلا حد زمني، والمنتجات والفئات فيه غير محدودة. والخطة المدفوعة تضيف لغات غير محدودة وأعضاء فريق أكثر ونطاقاً خاصاً وإدارة الطلبات والحجوزات من لوحة التحكم وسجل إحصاءات أطول.' },
      { q: 'كم لغة يمكن أن تحمل القائمة؟', a: 'سبع: الإنجليزية والفرنسية والعربية والإسبانية والبرتغالية والألمانية والإيطالية، مع عرض حقيقي من اليمين إلى اليسار للعربية. الخطة المجانية تغطي لغتين في آن واحد، والمدفوعة ترفع الحد.' },
      { q: 'هل يمكن استقبال الطلبات بلا نظام نقاط بيع؟', a: 'نعم. تصل الطلبات عبر واتساب كرسالة منظَّمة ومكتوبة سلفاً، أو عبر رموز QR الخاصة بالطاولات إلى تدفق في لوحة التحكم. لا تُقتطع عمولة وتبقى طريقة تحصيلك الحالية كما هي.' },
      { q: 'هل يحتاج الزبون حساباً ليطلب أو يقيّم أو يجمع الأختام؟', a: 'لا. إرسال الطلب، ومتابعة حالته بعد ذلك، وترك تقييم، وملء بطاقة الولاء، كلها تتم على صفحة القائمة نفسها، بلا تطبيق وبلا حساب وبلا جمع أي رقم هاتف.' },
      { q: 'هل تصلح لمحل ليس مطعماً؟', a: 'نعم. الكتالوج يتصرف كالقائمة: بقالات وجزارات وصيدليات وصالونات ومتاجر ألبسة وإلكترونيات تستخدم الفئات والمنتجات والأسعار نفسها، وتعطّل الطلب إن كانت تريد قائمة أسعار للاطلاع فقط.' },
    ],
    conclusion: [
      'القائمة الرقمية لا تغيّر طريقة إدارتك لمحلك. هي تنقل شيئاً واحداً محدداً، قائمة أسعارك، من الورق (حيث يكلّف تعديلها مالاً وانتظاراً) إلى صفحة تحرّرها من هاتفك بين خدمتين.',
      'وما يخرج من ذلك عادي ومفيد. أسعار صحيحة، وأطباق نافدة تُعلَّم في ثوانٍ، وعدة لغات خلف رمز واحد، وطلبات مقروءة، وفكرة أولى عمّا يقرأه الناس قبل أن يختاروا.',
      {
        parts: [
          { text: 'إن كانت قائمتك على وشك العودة إلى المطبعة، فهذه هي اللحظة المناسبة لتجربة شيء آخر. إنشاء ' },
          { text: 'قائمة رمز QR للمطاعم والمحلات', href: 'https://scanqur.com/ar' },
          { text: ' وطباعة رمزها لا يكلّفان شيئاً، وأسوأ الاحتمالات أن تنتهي برابط محدَّث تعطيه إلى جانب القائمة المطبوعة التي كنت ستطلبها على أي حال.' },
        ],
      },
    ],
  },
];
